import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { recuentosAPI, ESTADOS, formatFechaHora } from '@/lib/recuentos';
import { EstadoSemanalCard } from './EstadoSemanalCard';
import { NuevoRecuentoDialog } from './NuevoRecuentoDialog';

const RecuentoListItem = ({ r, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3 text-left hover:border-orange-300 transition-colors"
    data-testid={`mi-recuento-${r.id}`}
  >
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-slate-800 truncate">{r.lugar_nombre}</div>
      <div className="text-xs text-slate-500">{formatFechaHora(r.created_at)} · {r.ingredientes_contados}/{r.total_ingredientes} contados</div>
    </div>
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ESTADOS[r.estado]?.className}`}>{ESTADOS[r.estado]?.label}</span>
    <ChevronRight className="w-4 h-4 text-slate-400" />
  </button>
);

export default function RecuentosHomePage({ basePath }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lugares, setLugares] = useState([]);
  const [recuentos, setRecuentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    Promise.all([recuentosAPI.misLugares(), recuentosAPI.list({ limit: 20 })])
      .then(([l, r]) => { setLugares(l.data); setRecuentos(r.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-slate-500">Cargando...</div>;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-3xl mx-auto" data-testid="recuentos-home-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-orange-500" /> Recuentos
          </h1>
          <p className="text-sm text-slate-500">Conteo físico semanal para Justo (cada lunes antes de abrir)</p>
        </div>
        {lugares.length > 0 && (
          <Button onClick={() => setDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600 h-11" data-testid="nuevo-recuento-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo recuento
          </Button>
        )}
      </div>

      {lugares.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800" data-testid="sin-lugares-msg">
          No tienes lugares de recuento asignados. Pide al administrador que te los asigne en Usuarios.
        </div>
      ) : (
        <EstadoSemanalCard filterCodigos={lugares.map((l) => l.codigo)} onOpen={(id) => navigate(`${basePath}/${id}`)} />
      )}

      <div className="space-y-2">
        <h2 className="text-base font-semibold text-slate-700">Mis recuentos</h2>
        {recuentos.length === 0 && <p className="text-sm text-slate-500" data-testid="sin-recuentos-msg">Aún no hay recuentos.</p>}
        {recuentos.map((r) => <RecuentoListItem key={r.id} r={r} onClick={() => navigate(`${basePath}/${r.id}`)} />)}
      </div>

      <NuevoRecuentoDialog open={dialogOpen} onOpenChange={setDialogOpen} lugares={lugares} basePath={basePath} defaultResponsable={user?.name} />
    </div>
  );
}
